export interface GenerateFormsPayload {
  modelResultIds: number[];
  modelResultGroups?: number[][];
}

/** Output shape sent to the backend `generate-draft-pdf` endpoint. */
export interface GenerateFormsDraftPdfPayload {
  mod?: string;
  ejf?: string;
  fic?: string;
  idi?: string;
}

/**
 * Input shape accepted by `generate210formDraftPdfBase64` and
 * `buildGenerateFormsDraftPdfPayload`. All fields are optional so callers
 * that only know some fields at build time are not forced to supply them all.
 * `periodo` is accepted from call sites but intentionally not forwarded — the
 * backend `generate-draft-pdf` endpoint does not consume it.
 */
export interface GenerateDraftPdfInput {
  mod?: string;
  /** Year — may arrive as a number from component state; coerced to string before sending. */
  ejf?: string | number;
  fic?: string;
  idi?: string;
  /** Present in some call sites but not consumed by the backend endpoint. */
  periodo?: string;
}

function toNumber(value: unknown): number | undefined {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function toStringValue(value: unknown): string | undefined {
  if (value === null || value === undefined) {
    return undefined;
  }
  const text = String(value).trim();
  return text.length > 0 ? text : undefined;
}

export function buildGenerateFormsPayload(input: any): GenerateFormsPayload {
  const ids = Array.isArray(input?.modelResultIds)
    ? input.modelResultIds
        .map((value: unknown) => toNumber(value))
        .filter((value: number | undefined): value is number => value !== undefined)
    : [];
  const groups = Array.isArray(input?.modelResultGroups)
    ? input.modelResultGroups
        .filter((group: unknown) => Array.isArray(group))
        .map((group: unknown[]) =>
          group
            .map((value: unknown) => toNumber(value))
            .filter((value: number | undefined): value is number => value !== undefined),
        )
        .filter((group: number[]) => group.length > 0)
    : [];

  return {
    modelResultIds: ids,
    ...(groups.length > 0 ? { modelResultGroups: groups } : {}),
  };
}

export function buildGenerateFormsDraftPdfPayload(input: GenerateDraftPdfInput): GenerateFormsDraftPdfPayload {
  const payload: GenerateFormsDraftPdfPayload = {};

  const mod = toStringValue(input?.mod);
  if (mod !== undefined) payload.mod = mod;

  const ejf = toStringValue(input?.ejf);
  if (ejf !== undefined) payload.ejf = ejf;

  const fic = toStringValue(input?.fic);
  if (fic !== undefined) payload.fic = fic;

  const idi = toStringValue(input?.idi);
  if (idi !== undefined) payload.idi = idi;

  return payload;
}
